// Class that represent the BIMSearch engine
// Connected to a specific BIM360 view
// Allow running filters and creating schedules on elements in the view

import { PropertiesManager } from "./PropertiesManager.js";
import { DataForMenus, QuickDataField, operatorValues, MenusSelection, DataForTable, QuickInfo } from "./ResultClasses";
import { Template, BimsearchElement, modelIdToKey } from "./TemplatesAndCollections";
import { logError } from "./IAmecLog";
import { getFromServer, postToServer } from "./CommonFunctions.js";

// Main class for BIMSearch filter and data search
export class BimSearch {
    // public:
    constructor() {
        this.init();
    }

    init() {
        this.selectedCategoryIds = [];
        this.selectedCategories = [];
        this.categoryIdNameDictionary = {};
        this.sharedPropertiesDictionary = {};
        this.allElements = [];
        this.propertiesManager = new PropertiesManager();
        this.modelName = "";
        this.selectedFilter = "Not selected";
        this.categoryElements = null;
        this.filteredElements = null;
        this.commonCategories = [...CommonCategories];
        this.commonProperties = [...CommonProperties];
        this.commonGroupingOptions = [...CommonGroupingOptions];
        this.nonSummableProperties = null;
        this.userTemplates = null; // Dictionary string --> [] of BimsearchTemplate
    }

    async loadModel(viewer, objectsTree, modelName, projectId) {
        this.init();
        this.modelName = modelName;

        objectsTree.enumNodeChildren(
            objectsTree.getRootId(),
            (nodeId) => {
                this.allElements.push(nodeId);
            },
            true
        );

        await this.propertiesManager.loadElements(viewer, this.allElements, projectId);

        this.updatePropertiesTranslations();

        var categories = [];
        var categoryIds = [];
        var templatesCount = [];
        var templates = [];
        var templatesFolder = new Object();
        this.userTemplates = await getUserTemplates();
        
        Object.keys(this.userTemplates).forEach(key => {
            this.userTemplates[key].sort((a, b) => a.title < b.title ? -1 : 1);
            templatesFolder[key] = [];
            this.userTemplates[key].forEach(temp => {
                templatesFolder[key].push(temp.title);
                templates.push(temp.title);
                templatesCount.push(0);
            });
        });
        
        for (var i = 0; i < this.allElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.allElements[i]);
            if (props == null) continue;

            var category = this.findByDisplayName(props, CategoryParameterName);

            var categoryId = this.findByDisplayName(props, "CategoryId");

            if (category) {
                var categoryName = category.displayValue;
                if (categoryName.startsWith(categoryPre))
                    categoryName = categoryName.slice(categoryPre.length);
                if (!categories.includes(categoryName)) {
                    categories.push(categoryName);

                    if (categoryId != undefined) {
                        if (!(categoryId.displayValue in this.categoryIdNameDictionary)) {
                            this.categoryIdNameDictionary[categoryId.displayValue] = categoryName;
                            categoryIds.push(categoryId);
                        }
                    }
                }

            }
        }

        this.updateCategoriesTranslations();

        var res;

        if (categories.length == 0) {
            res = await this.selectCategory(null, null);
        } else {
            categories.sort();
            res = new DataForMenus();

        }

        res.templates = templates;
        res.templatesCount = templatesCount;
        res.templatesFolder = templatesFolder;

        res.categories = categories;
        this.splitCommon(res);

        return res;
    }

    async selectCategory(categories, categoryIds) {
        if (categoryIds == null) {
            categoryIds = [];
        }

        this.selectedCategories = categories;
        this.selectedCategoryIds = categoryIds;

        this.selectedFilter = "Not selected";

        var res = new DataForMenus();

        this.categoryElements = await this.findElementsByCategories(categories, categoryIds);
        
        var sharedProperties = this.getSharedPropertiesByDisplayName(this.categoryElements);

        this.filteredElements = this.categoryElements;

        var counterData = this.getCounterData(this.categoryElements);

        res.quickCounterData = counterData;
        res.filterByOptions = sharedProperties;
        res.properties = sharedProperties;
        res.groupingOptions = sharedProperties;
        res.filterValues = [];

        this.splitCommon(res);

        return res;
    }

    async findElementsByCategories(categories, categoryIds) {
        var res = [];
        
        if (categories == null) {
            res = this.allElements;
        } else if (categories.length > 0) {
            for (var i = 0; i < this.allElements.length; i++) {
                var props = this.propertiesManager.GetProperties(this.allElements[i]);

                var categoryPropIds = this.findByDisplayName(props, "CategoryId");
                var categoryPropNames = this.findByDisplayName(props, CategoryParameterName); 

                var elementDetected = false;

                if (categoryPropIds != null && categoryIds.length > 0) {
                    if (categoryIds.includes(categoryPropIds.displayValue)) {
                        res.push(this.allElements[i]);
                        elementDetected = true;
                    }
                }
                else if (!elementDetected && categoryPropNames != null && categories.length > 0) {
                    if (categories.includes(categoryPropNames.displayValue.replace(categoryPre, ''))) {
                        res.push(this.allElements[i]);
                        elementDetected = true;
                    }
                }
                else if (!elementDetected && categoryPropIds != null && categories.length > 0) {
                    if (res.includes(this.categoryIdNameDictionary[categoryPropIds.displayValue])) {
                        res.push(this.allElements[i]);
                        elementDetected = true;
                    }
                }
            }
        }
        return res;
    }

    async changeFilterBy(filter) {
        this.selectedFilter = filter;
        var filterValues = [];
        var allNumbers = true;

        for (var i = 0; i < this.categoryElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.categoryElements[i]);
            var prop = this.findByDisplayName(props, filter);

            if (prop) {
                var value = prop.displayValue;
                if (!filterValues.includes(value)) {
                    if (typeof value != "number")
                        allNumbers = false;
                    filterValues.push(value);
                }
            }
        }

        if (allNumbers)
            filterValues.sort(function (a, b) {
                return a - b;
            });
        else filterValues.sort();

        filterValues.unshift("All");

        var res = new DataForMenus();
        res.filterValues = filterValues;
        return res;
    }

    async selectSecondFilterValues(values) {
        if (values.includes("All")) {
            this.filteredElements = this.categoryElements;
        } else {
            this.filteredElements = [];

            for (var i = 0; i < this.categoryElements.length; i++) {
                var props = this.propertiesManager.GetProperties(this.categoryElements[i]);
                var elementValue = this.findByDisplayName(props, this.selectedFilter);

                var passFilter =
                    elementValue && values.includes(elementValue.displayValue.toString());

                if (passFilter) this.filteredElements.push(this.categoryElements[i]);
            }
        }

        return this.getMenusDataAfterSecondFilter();
    }

    async selectSecondFilterValueWithOperator(operator, value) {
        this.filteredElements = [];

        for (var i = 0; i < this.categoryElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.categoryElements[i]);
            var elementprop = this.findByDisplayName(props, this.selectedFilter);

            if (elementprop && elementprop.displayValue != null) {
                var elementValue = elementprop.displayValue;
                var passFilter = false;

                switch (operator) {
                    case operatorValues.equal:
                        passFilter = elementValue == value;
                        break;
                    case operatorValues.notEqual:
                        passFilter = elementValue != value;
                        break;
                    case operatorValues.greater:
                        passFilter = elementValue > value;
                        break;
                    case operatorValues.less:
                        passFilter = elementValue < value;
                        break;
                    case operatorValues.greaterOrEqual:
                        passFilter = elementValue >= value;
                        break;
                    case operatorValues.lessOrEqual:
                        passFilter = elementValue <= value;
                        break;
                    default:
                        passFilter = false;
                }

                if (passFilter) this.filteredElements.push(this.categoryElements[i]);
            }
        }

        return this.getMenusDataAfterSecondFilter();
    }

    async selectTemplate(templateName, templateFolder = "Default") {
        
        var selectedTemplate;

        for (var i = 0; i < this.userTemplates[templateFolder].length; i++) {
            if (this.userTemplates[templateFolder][i].title == templateName) {
                selectedTemplate = this.userTemplates[templateFolder][i];
                break;
            }
        }

        var res = await this.selectCategory(selectedTemplate.categories, selectedTemplate.categoryIds);

        //var temp = await this.changeFilterBy(selectedTemplate.secondFilter);
        //res.filterValues = temp.filterValues;

        //temp = await this.selectSecondFilterValues(selectedTemplate.filterValues);
        //res.properties = temp.properties;
        //res.groupingOptions = temp.groupingOptions;
        //res.quickCounterData = temp.quickCounterData;

        var sharedPropertiesByDisplayName = this.getSharedPropertiesByDisplayName(this.filteredElements);
        var sharedPropertiesByAttributeName = this.getSharedPropertiesByAttributeName(this.filteredElements);

        var existingProps = [];
        var existingGroups = [];

        for (var i = 0; i < selectedTemplate.properties.length; i++) {
            var propDisplayName = null;

            if (sharedPropertiesByAttributeName.includes(selectedTemplate.properties[i])) {
                propDisplayName = this.sharedPropertiesDictionary[selectedTemplate.properties[i]];
            } else if (sharedPropertiesByDisplayName.includes(selectedTemplate.properties[i])) {
                propDisplayName = selectedTemplate.properties[i];
            }

            if (propDisplayName != null && !existingProps.includes(propDisplayName)) {
                existingProps.push(propDisplayName);
            }
        }

        for (var i = 0; i < selectedTemplate.groupBy.length; i++) {
            if (sharedPropertiesByAttributeName.includes(selectedTemplate.groupBy[i])) {
                existingGroups.push(this.sharedPropertiesDictionary[selectedTemplate.groupBy[i]]);
            }
            else if (sharedPropertiesByDisplayName.includes(selectedTemplate.groupBy[i])) {
                existingGroups.push(selectedTemplate.groupBy[i]);
            }
        }

        var localizedCategoryNames = selectedTemplate.categoryIds.map(id => this.categoryIdNameDictionary[id]);

        res.selection = new MenusSelection(
            localizedCategoryNames,
            selectedTemplate.secondFilter,
            selectedTemplate.filterValues,
            existingProps,
            existingGroups,
            selectedTemplate.searchValue
        );

        return res;
    }

    async createSchedule(properties, groupingParameters) {
        if (properties.includes("All"))
            properties = this.getSharedPropertiesByDisplayName(this.filteredElements);

        if (groupingParameters.length == 0) {
            return await this.getInstancesSchedule(properties);
        }

        // find the groups
        var groups = [];
        var groupNames = [];
        groups.push(this.filteredElements);
        groupNames.push("");

        for (var i = 0; i < groupingParameters.length; i++) {
            var newgroups = [];
            var newGroupNames = [];

            for (var j = 0; j < groups.length; j++) {
                for (var k = 0; k < groups[j].length; k++) {
                    var props = this.propertiesManager.GetProperties(groups[j][k]);
                    var prop = this.findByDisplayName(props, groupingParameters[i]);
                    var value = prop ? prop.displayValue : "Missing Parameter";
                    var name = groupNames[j] + "[" + value + "]";
                    if (newGroupNames.includes(name)) {
                        newgroups[newGroupNames.indexOf(name)].push(groups[j][k]);
                    } else {
                        var g = [];
                        g.push(groups[j][k]);
                        newgroups.push(g);
                        newGroupNames.push(name);
                    }
                }
            }

            groups = newgroups;
            groupNames = newGroupNames;
        }

        // sort groups by group names
        var nonSortedNames = [...groupNames];
        groupNames.sort();
        var sortedGroups = [];
        for (var i = 0; i < groupNames.length; i++) {
            sortedGroups.push(groups[nonSortedNames.indexOf(groupNames[i])]);
        }
        groups = sortedGroups;

        var sums = [];
        var units = [];

        for (var i = 0; i < properties.length; i++) {
            sums.push(null);
            units.push(null);
        }

        var groupLines = [];

        for (var i = 0; i < groups.length; i++) {
            var line = [];
            line.push(groupNames[i]);
            line.push(groups[i].length);

            for (var j = 0; j < properties.length; j++) {
                var sum = 0;
                var allSame = true;
                var isNumber = false;
                var sameValue = null;

                for (var k = 0; k < groups[i].length; k++) {
                    var props = this.propertiesManager.GetProperties(groups[i][k]);
                    var prop = this.findByDisplayName(props, properties[j]);
                    var value = prop ? prop.displayValue : "";

                    if (typeof value == "number") {
                        var eUnits = true;
                        if (prop.units != null) eUnits = prop.units;
                        if (units[j] == null) units[j] = eUnits;
                        else if (units[j] != eUnits && value != 0) units[j] = "Mixed units";
                    }
                    if (typeof value == "number" && this.isSummable(properties[j])) {
                        allSame = false;
                        isNumber = true;
                        sum += value;

                    } else if (allSame) {
                        if (sameValue == null) sameValue = value;
                        allSame = sameValue == value;
                    }
                }

                if (allSame) line.push(sameValue);
                else if (isNumber) {
                    line.push(sum);
                    sums[j] = sums[j] == null ? sum : sums[j] + sum;
                } else line.push("");
            }
            groupLines.push(line);
        }

        var groupHeader = "";
        for (var i = 0; i < groupingParameters.length; i++)
            groupHeader += "[" + groupingParameters[i] + "]";

        var res = new DataForTable();
        res.header.push(groupHeader);
        res.units.push(null);
        res.header.push("Count");
        res.units.push(null);


        for (var i = 0; i < properties.length; i++) {
            var propText = properties[i];
            var unit = null;

            if (sums[i] != null) propText += " Σ";

            if (units[i] != null && units[i] != true) {
                var funit = formatUnit(units[i]);
                propText += " (" + funit + ")";
                if (funit != "Mixed units")
                    unit = funit;
            }

            res.header.push(propText);
            res.units.push(unit);
        }

        res.lineElements = groups;

        for (var i = 0; i < groupLines.length; i++) {
            var tableLine = [];

            for (var j = 0; j < groupLines[i].length; j++) {
                var value = groupLines[i][j];

                tableLine.push(value);
            }
            res.lines.push(tableLine);
        }

        res.totals.push("Total:");
        res.totals.push(this.filteredElements.length);

        for (var j = 0; j < properties.length; j++) {
            var totalValue = "";
            if (sums[j] != null) {
                totalValue = sums[j];
            }
            res.totals.push(totalValue);
        }

        return res;
    }

    async getTemplateInfo(templateName, TemplateFolder) {
        //Find template
        var selectedTemplate;

        if (this.userTemplates[TemplateFolder] != null)
            selectedTemplate = this.userTemplates[TemplateFolder].find(element => element.title == templateName)

        if (selectedTemplate == undefined) {
            logError("FrontEnd.utils.builder.Bimsearch.selectedTemplate", "Can't find template - " + templateName);
            throw ("can't find Template");
        }

        var categoryElements = await this.findElementsByCategories(selectedTemplate.categories, selectedTemplate.categoryIds);

        return new QuickInfo(selectedTemplate.creationTime, this.getCounterData(categoryElements), selectedTemplate.properties, selectedTemplate.description);
    }

    async getCollectionInfo(collection) {
        var quickData = [];
        quickData.push(new QuickDataField("Count", collection.elements.length));
        collectionDefaultProperties.forEach(prop => {
            var sum = 0;
            var unit = null;
            collection.elements.forEach(element => {
                var propValue = element.propertiesValues[prop];
                if (propValue != null) {
                    sum += propValue.displayValue;
                    if (unit == null)
                        unit = propValue.units;
                    else if (propValue.units != unit && propValue.displayValue != 0)
                        unit = "Mixed";
                }
            });
            quickData.push(new QuickDataField(prop, sum, formatUnit(unit)));
        });
        return new QuickInfo(collection.creationTime, quickData, collection.properties, collection.descreption);
    }

    getDetailedCollectionInfo(collection) {
        var res = new Object();
        collection.elements.forEach(element => {
            var category = "Undefined";
            var elementCategory = element.propertiesValues[CategoryParameterName];
            if (elementCategory != null && elementCategory.displayValue != null)
                category = elementCategory.displayValue;

            if (res[category] == null) {
                res[category] = [];
                res[category].push(new QuickDataField("Count"))
                collectionDefaultProperties.forEach(prop => {
                    res[category].push(new QuickDataField(prop));
                });
            }

            ++res[category][0].value;

            for (var i = 0; i < collectionDefaultProperties.length; i++) {
                var elementProp = element.propertiesValues[collectionDefaultProperties[i]];
                if (elementProp != null && elementProp.displayValue != null && typeof elementProp.displayValue == "number") {
                    res[category][i+1].value += elementProp.displayValue;
                    if (elementProp.units != null) {
                        var unit = formatUnit(elementProp.units);
                        if (res[category][i+1].unit == null)
                            res[category][i + 1].unit = unit;
                        else if (res[category][i + 1].unit != unit)
                            res[category][i+1].unit = "Mixed";
                    }
                }
            }
        });

        return res;
    }

    addTemplateFolder(folderName) {
        if (Object.keys(this.userTemplates).includes(folderName))
            throw ("Folder already exist");
        else {
            this.userTemplates[folderName] = [];
            this.updateTemplatesFolderOnDatabase();
        }
        
        return this.getTemplateNames();
    }

    deleteTemplateFolder(folderName) {
        if (folderName == DefaultTemplatesnFolder)
            throw "Can't delete default templates folder";

        if (!Object.keys(this.userTemplates).includes(folderName))
            throw ("Folder doesn't exist");
        else {
            delete this.userTemplates[folderName];
            this.updateTemplatesFolderOnDatabase();
        }

        return this.getTemplateNames();
    }

    renameTemplateFolder(originalName, newName) {
        if (originalName == DefaultTemplatesnFolder)
            throw "Can't rename default templates folder";

        if (Object.keys(this.userTemplates).includes(newName))
            throw ("Folder already exist");
        if (!Object.keys(this.userTemplates).includes(originalName))
            throw ("Folder doesn't exist");

        this.userTemplates[newName] = this.userTemplates[originalName];
        delete this.userTemplates[originalName];
        this.updateTemplatesFolderOnDatabase();

        return this.getTemplateNames();
    }

    getCounterData(elements) {
        var res = [];
        res.push(new QuickDataField("Count"));
        res.push(new QuickDataField("Length"));
        res.push(new QuickDataField("Area"));
        res.push(new QuickDataField("Volume"));

        for (var i = 0; i < elements.length; i++) {
            var props = this.propertiesManager.GetProperties(elements[i]);
            this.addElementToDataArray(res, props);
        }

        return res;
    }

    getDataByCategory(elements) {
        var res = new Object();

        for (var i = 0; i < elements.length; i++) {
            var props = this.propertiesManager.GetProperties(elements[i]);
            var categoryProp = this.findByDisplayName(props, CategoryParameterName); 

            var categoryName = "Unknown";
            if (categoryProp != null)
                categoryName = categoryProp.displayValue;

            if (res[categoryName] == null) {
                res[categoryName] = [];
                res[categoryName].push(new QuickDataField("Count"));
                res[categoryName].push(new QuickDataField("Length"));
                res[categoryName].push(new QuickDataField("Area"));
                res[categoryName].push(new QuickDataField("Volume"));
            }

            this.addElementToDataArray(res[categoryName], props);
        }

        return res;
    }

    saveTemplate(templateFolder, title, description, properties, groupBy, searchValue) {
        if (this.userTemplates[templateFolder] == null)
            this.userTemplates[templateFolder] = [];

        this.deleteTemplateNoSync(templateFolder, title);

        var categoriesIds = [];

        Object.keys(this.categoryIdNameDictionary).forEach(categoryId => {
            if (this.selectedCategories.includes(this.categoryIdNameDictionary[categoryId]))
                categoriesIds.push(Number(categoryId));
        });

        var template = new Template(title, description, this.selectedCategories, categoriesIds, properties, groupBy, searchValue);

        this.userTemplates[templateFolder].push(template);
        this.updateTemplatesFolderOnDatabase();

        return this.getTemplateNames();
    }

    deleteTemplate(templateFolder, title) {
        if (!this.deleteTemplateNoSync(templateFolder, title))
            throw "can't find template";
        this.updateTemplatesFolderOnDatabase();

        return this.getTemplateNames();
    }

    editTemplate(ogrinalTemplateFolder, originalTitle, newTemplateFolder, newTitle, newDescription) {

        var template;
        if (this.userTemplates[ogrinalTemplateFolder] != null) 
            template = this.userTemplates[ogrinalTemplateFolder].find(element => element.title == originalTitle);
            

        if (template == undefined)
            throw "can't find template";

        if (!(ogrinalTemplateFolder == newTemplateFolder && originalTitle == newTitle) && this.userTemplates[newTemplateFolder].findIndex(element => element.title == newTitle) != -1) {
            throw "new name already taken";
        }

        this.deleteTemplateNoSync(ogrinalTemplateFolder, originalTitle);

        template.description = newDescription;
        template.title = newTitle;

        if (this.userTemplates[newTemplateFolder] == null)
            this.userTemplates[newTemplateFolder] = [];

        this.userTemplates[newTemplateFolder].push(template);

        this.updateTemplatesFolderOnDatabase();
        return this.getTemplateNames();
    }

    async addElementsToCollection(collection, elements) {
        var elementProperties = [...collection.properties];
        collection.groupBy.forEach(prop => {
            if (!elementProperties.includes(prop))
                elementProperties.push(prop);
        });
        collectionDefaultProperties.forEach(prop => {
            if (!elementProperties.includes(prop))
                elementProperties.push(prop);
        });
        if (!elementProperties.includes(CategoryParameterName))
            elementProperties.push(CategoryParameterName);
            
        elements.forEach(element => {
            var collectionElement = new BimsearchElement(this.propertiesManager.viewerIdToModelId[element]);
            var props = this.propertiesManager.properties[element];
            elementProperties.forEach(prop => {
                collectionElement.propertiesValues[prop] = this.findByDisplayName(props, prop);
            });

            collection.elements.push(collectionElement);
        });
    }


    async removeElementsFromCollection(collection, elements) {
        var newElements = collection.elements.filter(element => !elements.includes(this.propertiesManager.modelIdToViewerId[modelIdToKey(element.id)]) );
        collection.elements = newElements;
    }


    async getCollectionTable(collection) {
        var res = new DataForTable();

        var sums = [];
        var units = [];

        for (var i = 0; i < collection.properties.length; i++) {
            sums.push(null);
            units.push(null);
        }

        for (var i = 0; i < collection.elements.length; i++) {
            for (var j = 0; j < collection.properties.length; j++) {
                var prop = collection.elements[i].propertiesValues[collection.properties[j]];

                if (prop) {
                    if (typeof prop.displayValue == "number") {
                        var eUnits = true;
                        if (prop.units != null) eUnits = prop.units;
                        if (units[j] == null) units[j] = eUnits;
                        else if (units[j] != eUnits && prop.displayValue != 0)
                            units[j] = "Mixed units";
                    }
                    if (typeof prop.displayValue == "number" && this.isSummable(collection.properties[j])) {
                        sums[j] = sums[j] == null ? prop.displayValue : sums[j] + prop.displayValue;
                    } 
                }
            }
        }

        if (collection.groupBy != null && collection.groupBy.length > 0) {
            var groupHeader = "";
            for (var i = 0; i < collection.groupBy.length; i++)
                groupHeader += "[" + collection.groupBy[i] + "]";

            res.header = [groupHeader, "Count"];
            res.units = [null, null];
            res.totals.push("Total:");
            res.totals.push(collection.elements.length);


            // find the groups
            var groups = [];
            var groupNames = [];
            groups.push(collection.elements);
            groupNames.push("");

            for (var i = 0; i < collection.groupBy.length; i++) {
                var newgroups = [];
                var newGroupNames = [];

                for (var j = 0; j < groups.length; j++) {
                    for (var k = 0; k < groups[j].length; k++) {
                        var props = this.propertiesManager.GetProperties(groups[j][k]);
                        var prop = groups[j][k].propertiesValues[collection.groupBy[i]];
                        var value = prop ? prop.displayValue : "Missing Parameter";
                        var name = groupNames[j] + "[" + value + "]";
                        if (newGroupNames.includes(name)) {
                            newgroups[newGroupNames.indexOf(name)].push(groups[j][k]);
                        } else {
                            var g = [];
                            g.push(groups[j][k]);
                            newgroups.push(g);
                            newGroupNames.push(name);
                        }
                    }
                }
                groups = newgroups;
                groupNames = newGroupNames;
            }

            // sort groups by group names
            var nonSortedNames = [...groupNames];
            groupNames.sort();
            var sortedGroups = [];
            for (var i = 0; i < groupNames.length; i++) {
                sortedGroups.push(groups[nonSortedNames.indexOf(groupNames[i])]);
            }
            groups = sortedGroups;


            var groupLines = [];
            for (var i = 0; i < groups.length; i++) {
                var line = [];
                line.push(groupNames[i]);
                line.push(groups[i].length);

                for (var j = 0; j < collection.properties.length; j++) {
                    var sum = 0;
                    var allSame = true;
                    var isNumber = false;
                    var sameValue = null;

                    for (var k = 0; k < groups[i].length; k++) {
                        var props = this.propertiesManager.GetProperties(groups[i][k]);
                        var prop = groups[i][k].propertiesValues[collection.properties[j]];
                        var value = prop ? prop.displayValue : "";

                        if (typeof value == "number" && this.isSummable(collection.properties[j])) {
                            allSame = false;
                            isNumber = true;
                            sum += value;
                        } else if (allSame) {
                            if (sameValue == null)
                                sameValue = value;
                            allSame = sameValue == value;
                        }
                    }

                    if (allSame)
                        line.push(sameValue);
                    else if (isNumber) {
                        line.push(sum);
                    }
                    else
                        line.push("");
                }
                groupLines.push(line);
            }

            groups.forEach(group => {
                var lineElements = [];
                group.forEach(element => {
                    var viewerId = this.propertiesManager.modelIdToViewerId[modelIdToKey(element.id)];
                    if (viewerId != null)
                        lineElements.push(viewerId);
                });
                res.lineElements.push(lineElements);
            });

            res.lines = groupLines;
        }
        else {
            for (var i = 0; i < collection.elements.length; i++) {
                var lineElements = [];
                var viewerId = this.propertiesManager.modelIdToViewerId[modelIdToKey(collection.elements[i].id)];
                if (viewerId != null)
                    lineElements.push(viewerId);
                res.lineElements.push(lineElements);

                var lineData = [];
                lineData.push(collection.elements[i].id.revitId);

                for (var j = 0; j < collection.properties.length; j++) {
                    var prop = collection.elements[i].propertiesValues[collection.properties[j]];
                    var value = "";
                    if (prop != null && prop.displayValue != null)
                        value = prop.displayValue;
                    lineData.push(value);
                }
                res.lines.push(lineData);
            }

            res.header.push("Element");
            res.units.push(null);
            res.totals.push("Total: " + collection.elements.length);
        }

        for (var i = 0; i < collection.properties.length; i++) {
            var headerProp = collection.properties[i];
            var unit = null;
            if (sums[i] != null)
                headerProp += " Σ";

            if (units[i] == "Mixed units")
                headerProp += " (Mixed units)";
            if (units[i] != true && units[i] != "Mixed units")
                unit = formatUnit(units[i]);
            res.header.push(headerProp);
            res.units.push(unit);

            var totalVal = "";
            if (sums[i] != null) {
                totalVal = sums[i];
            }
            res.totals.push(totalVal);
        }

        return res;
    }

    // Private:


    // deletes the template without Syncing with cloud
    // return true if an element was deleted false if it was not found
    deleteTemplateNoSync(templateFolder, title) {
        var index = -1;
        if (this.userTemplates[templateFolder] != null) 
            index = this.userTemplates[templateFolder].findIndex(element => element.title == title);

        if (index >= 0)
            this.userTemplates[templateFolder].splice(index, 1);

        return index >= 0;
    }

    getTemplateNames() {
        var res = new Object();

        Object.keys(this.userTemplates).forEach(key => {
            this.userTemplates[key].sort((a, b) => a.title < b.title ? -1 : 1);
            res[key] = [];
            this.userTemplates[key].forEach(temp => {
                res[key].push(temp.title);
            });
        });

        return res;
    }

    async updateTemplatesFolderOnDatabase() {
        try {
            postToServer("/api/forge/userdata/templates", this.userTemplates);
        }
        catch (err) {
            logError("FrontEnd.src.utils.builder.BimSearch.updateTemplatesFolderOnDatabase", err.toString());
        }
    }

    getSharedPropertiesByDisplayName(ids) {
        var propertyNames = [];
        var properties = this.getValidProperties(ids);
        var propertyNames = properties.map(property => property.displayName);

        if (ids.length > 0) {
            var additionalParameterNames = this.getAdditionalParameterNames();

            for (var i = 0; i < additionalParameterNames.length; i++) {
                propertyNames.push(additionalParameterNames[i]);
            }
        }

        propertyNames.sort();
        var uniquePropertyNames = [...new Set(propertyNames)];

        return uniquePropertyNames;
    }

    getSharedPropertiesByAttributeName(ids) {
        var propertyNames = [];
        var properties = this.getValidProperties(ids);
        var propertyNames = properties.map(property => property.attributeName);

        if (ids.length > 0) {
            var additionalParameterNames = this.getAdditionalParameterNames();

            for (var i = 0; i < additionalParameterNames.length; i++) {
                propertyNames.push(additionalParameterNames[i]);
            }
        }

        propertyNames.sort();
        var uniquePropertyNames = [...new Set(propertyNames)];

        return uniquePropertyNames;
    }

    getAdditionalParameterNames() {
        var propNames = [CategoryParameterName, LinkDemiParameterName, FamilyDemiParameterName].concat(wbsParameters);
        return this.isRvt() ? propNames : [];
    }

    getValidProperties(ids) {
        var properties = [];
        var proprertyNames = [];

        for (var i = 0; i < ids.length; i++) {
            var props = this.propertiesManager.GetProperties(ids[i]);

            if (props && props.properties && props.properties.length != null)
                for (var j = 0; j < props.properties.length; j++)
                    if (
                        (!proprertyNames.includes(props.properties[j].displayName) || !proprertyNames.includes(props.properties[j].attributeName)) &&
                        !props.properties[j].hidden &&
                        props.properties[j].displayValue != null &&
                        props.properties[j].displayValue !== "" &&
                        ((this.isRvt() && !props.properties[j].hidden) ||
                            (!this.isRvt() && !props.properties[j].displayName.startsWith("Current Control point -") && !props.properties[j].displayName.startsWith("Vertex -")))
                    ) {
                        properties.push(props.properties[j]);
                        proprertyNames.push(props.properties[j].attributeName);
                        proprertyNames.push(props.properties[j].displayName);
                    }
        }

        return properties;
    }

    updatePropertiesTranslations() {
        var ids = this.allElements;
        var propsDictionary = [];

        for (var i = 0; i < ids.length; i++) {
            var props = this.propertiesManager.GetProperties(ids[i]);

            if (props && props.properties && props.properties.length != null)
                for (var j = 0; j < props.properties.length; j++)
                    if (!(props.properties[j].attributeName in propsDictionary) &&
                        !props.properties[j].hidden &&
                        props.properties[j].displayValue != null &&
                        props.properties[j].displayValue !== ""
                    ) {
                        propsDictionary[props.properties[j].attributeName] = props.properties[j].displayName;
                    }
        }

        if (ids.length > 0) {
            var additionalParameterNames = this.getAdditionalParameterNames();
            for (var i = 0; i < additionalParameterNames.length; i++) {
                propsDictionary[additionalParameterNames[i]] = additionalParameterNames[i];
            }
        }

        propsDictionary.sort();

        this.sharedPropertiesDictionary = propsDictionary;

        for (var i = 0; i < CommonProperties.length; i++) {
            var value = this.sharedPropertiesDictionary[CommonProperties[i]];
            if (value != null && !this.commonProperties.includes(value))
                this.commonProperties.push(value);
        }

        for (var i = 0; i < CommonGroupingOptions.length; i++) {
            var value = this.sharedPropertiesDictionary[CommonGroupingOptions[i]];
            if (value != null && !this.commonGroupingOptions.includes(value))
                this.commonGroupingOptions.push(value);
        }

        this.nonSummableProperties = [...NonSummableProperties];
        for (var i = 0; i < NonSummableProperties.length; i++) {
            if (this.sharedPropertiesDictionary[NonSummableProperties[i]] != null) {
                this.nonSummableProperties.push(this.sharedPropertiesDictionary[NonSummableProperties[i]]);
            }
        }
    }

    isSummable(property) {
        return !this.nonSummableProperties.includes(property);
    }

    updateCategoriesTranslations() {
        for (var i = 0; i < CommonCategoriesIds.length; i++) {
            var value = this.categoryIdNameDictionary[CommonCategoriesIds[i]];
            if (value != null && !this.commonCategories.includes(value))
                this.commonCategories.push(value);
        }
    }

    findByDisplayName(props, value) {
        var res;
        if (value == LinkDemiParameterName) {
            res = new Object();
            res.displayValue = this.propertiesManager.GetModel(props.dbId);
            return res;
        }
        if (value == FamilyDemiParameterName) {
            res = new Object();
            var splitName = props.name.split(" [");
            res.displayValue = splitName.length > 1 ? splitName[0] : "";
            return res;
        }
        if (value == "wbs_ids") {
            res = new Object();
            res.displayValue = this.propertiesManager.GetWbsIds(props.dbId);
            return res;
        }
        if (value == "wbs_states") {
            res = new Object();
            res.displayValue = this.propertiesManager.GetWbsStates(props.dbId);
            return res;
        }
        if (value == "wbs_reportParameters") {
            res = new Object();
            res.displayValue = this.propertiesManager.GetWbsReportParameters(props.dbId);
            return res;
        }
        if (value == "wbs_assignmentValues") {
            res = new Object();
            res.displayValue = this.propertiesManager.GetWbsAssignmentValues(props.dbId);
            return res;
        }
        if (value == "wbs_billMonth") {
            res = new Object();
            res.displayValue = this.propertiesManager.GetWbsBillMonths(props.dbId);
            return res;
        }
        if (value == "wbs_descreption") {
            res = new Object();
            res.displayValue = this.propertiesManager.GetWbsDescreptions(props.dbId);
            return res;
        }
        if (value == "wbs_billDate") {
            res = new Object();
            res.displayValue = this.propertiesManager.getWbsBillDate(props.dbId);
            return res;
        }

        res = props.properties.find(
            (prop) => (prop.attributeName == value || prop.displayName == value) && prop.hidden == false
        );
        if (res == null) res = props.properties.find((prop) => prop.attributeName == value || prop.displayName == value);

        if (res == null && value == CategoryParameterName) {
            res = new Object();
            var sourceFileName = this.findByDisplayName(props, "Source File");
            if (sourceFileName != null && sourceFileName.displayValue.toLowerCase().endsWith("dwg"))
                res.displayValue = "DWG elements";
            else
                res.displayValue = ElementsWithoutCategoryCategory;
            return res;
        }

        return res;
    }

    getMenusDataAfterSecondFilter() {
        var res = new DataForMenus();
        var sharedProperties = this.getSharedPropertiesByDisplayName(this.filteredElements);

        var counterData = this.getCounterData(this.filteredElements);

        res.quickCounterData = counterData;
        res.properties = sharedProperties;
        res.groupingOptions = sharedProperties;

        return res;
    }

    isRvt() {
        return this.modelName.toLowerCase().endsWith(".rvt");
    }

    splitCommon(resultItem) {
        if (resultItem.categories != null) {
            resultItem.commonCategories = resultItem.categories.filter(category => this.commonCategories.includes(category));
            resultItem.uncommonCategories = resultItem.categories.filter(category => !this.commonCategories.includes(category));
        }

        if (resultItem.properties != null) {

            resultItem.commonProperties = resultItem.properties.filter(property => this.commonProperties.includes(property));
            resultItem.uncommonProperties = resultItem.properties.filter(property => !this.commonProperties.includes(property));
        }

        if (resultItem.groupingOptions != null) {

            resultItem.commonGroupingOptions = resultItem.groupingOptions.filter(property => this.commonGroupingOptions.includes(property));
            resultItem.uncommonGroupingOptions = resultItem.groupingOptions.filter(property => !this.commonGroupingOptions.includes(property));
        }

        return resultItem;
    }

    async getInstancesSchedule(properties) {
        var sums = [];
        var units = [];

        for (var i = 0; i < properties.length; i++) {
            sums.push(null);
            units.push(null);
        }

        var res = new DataForTable();

        for (var i = 0; i < this.filteredElements.length; i++) {
            var lineElements = [];
            lineElements.push(this.filteredElements[i]);
            res.lineElements.push(lineElements);

            var props = this.propertiesManager.GetProperties(this.filteredElements[i]);

            var lineData = [];
            lineData.push(props.name);

            for (var j = 0; j < properties.length; j++) {
                var prop = this.findByDisplayName(props, properties[j]);

                var value = "";

                if (prop) {
                    if (typeof prop.displayValue == "number") {
                        var eUnits = true;
                        if (prop.units != null) eUnits = prop.units;
                        if (units[j] == null) units[j] = eUnits;
                        else if (units[j] != eUnits && prop.displayValue != 0)
                            units[j] = "Mixed units";
                    }
                    if (typeof prop.displayValue == "number" && this.isSummable(properties[j])) {
                        value = prop.displayValue;
                        sums[j] = sums[j] == null ? prop.displayValue : sums[j] + prop.displayValue;

                    } else {
                        value = prop.displayValue;
                    }
                }
                lineData.push(value);
            }
            res.lines.push(lineData);
        }

        res.header.push("Element");
        res.units.push(null);

        for (var i = 0; i < properties.length; i++) {
            var headerProp = properties[i];
            var unit = null;
            if (sums[i] != null)
                headerProp += " Σ";

            if (units[i] != null && units[i] == "Mixed units")
                headerProp += " (Mixed units)";
            if (units[i] != null && units[i] != true && units[i] != "Mixed units")
                unit = formatUnit(units[i]);
            res.header.push(headerProp);
            res.units.push(unit);
        }

        res.totals.push("Total: " + this.filteredElements.length);

        for (var j = 0; j < properties.length; j++) {
            var totalVal = "";
            if (sums[j] != null) {
                totalVal = sums[j];
            }
            res.totals.push(totalVal);
        }

        return res;
    }
    
    addElementToDataArray(dataFieldArray, itemProps) {
        var areaParam = this.findByDisplayName(itemProps, "Area");
        var volumeParam = this.findByDisplayName(itemProps, "Volume");
        var lengthParam = this.findByDisplayName(itemProps, "Length");

        dataFieldArray[0].value++;
        addPropertyToDataField(dataFieldArray[1], lengthParam);
        addPropertyToDataField(dataFieldArray[2], areaParam);
        addPropertyToDataField(dataFieldArray[3], volumeParam);
    }
}

const categoryPre = "Revit ";
const LinkDemiParameterName = "Revit Link";
const FamilyDemiParameterName = "Family";
const CategoryParameterName = "Category";
const ElementsWithoutCategoryCategory = "No Category";
const NonSummableProperties = ["Diameter", "Tray Width", "Tray Height", "Thickness", "Angle", "Height", "Width", "Duct Width", "Duct Height", "H", "Railing Height", "Bottom Elevation", "Level", "ID", "Cost", "Unconnected Height", "Base Offset", "Top Offset"];
const collectionDefaultProperties = ["Length", "Area", "Volume"];

const wbsParameters = ["wbs_ids", "wbs_states", "wbs_reportParameters", "wbs_assignmentValues", "wbs_billMonth", "wbs_descreption", "wbs_billDate"];

const knownUnits = {
    centimeters: "cm",
    meters: "m",
    squareMeters: "m^2",
    cubicMeters: "m^3"
};

function formatUnit(unitString) {
    if (unitString == null)
        return null;
    var res = unitString;
    if (res.startsWith("autodesk.unit")) {
        var splitUnitString = res.split(":");
        splitUnitString.shift();
        res = splitUnitString.join(":");
        splitUnitString = res.split("-");
        splitUnitString.pop();
        res = splitUnitString.join("-");

        if (Object.keys(knownUnits).includes(res))
            res = knownUnits[res];
    }

    return res;
}

function roundNumber(number) {
    return Math.round(number * 100) / 100;
}

const CommonCategories = ["Structural Foundations", "Walls", "Floors", "Structural Columns", "Structural Framing",
    "Ceilings", "Doors", "Windows", "Curtain Walls", "Rooms", "Pipes", "Ducts", "Duct Fittings", "Cable Trays",
    "Air Terminals"];

const CommonCategoriesIds = [-2001300, -2000011, -2000032, -2001330, -2001320,
    -2000038, -2000023, -2000014, -2000160, -2008044, -2008000, -2008010, -2008130,
    -2008013];

const CommonProperties = ["Type Name", "Level", "Base Constraint", "Reference Level", "Base Level", "Description",
    "Area", "Volume", "Length", "Perimeter", "Diameter", "Type Mark", "Top Level", "Structural Material",
    "Thickness", "Revit Link", "Mark"].concat(wbsParameters);

const CommonGroupingOptions = ["Type Name", "Level", "Base Constraint", "Reference Level", "Base Level", "Description",
    "Diameter", "Type Mark", "Structural Material", "Thickness", "Revit Link", "System Type", "System Name"].concat(wbsParameters);

const DefaultTemplatesnFolder = "Default";



function addPropertyToDataField(dataField, property) {
    if (property != null && typeof property.displayValue == "number") {
        dataField.value += property.displayValue;
        if (property.units != null) {
            var units = formatUnit(property.units);
            if (dataField.unit == null) dataField.unit = units;
            else if (dataField.unit != units) dataField.unit = "Mixed";
        }
    }
}

// Get user Templates
async function getUserTemplates() {
    try {
        return await getFromServer("/api/forge/userdata/templates");
    }
    catch (err) {
        logError("FrontEnd.src.utils.builder.IScheduleLogic.getUserTemplates", err.toString());
        return new Object();
    }
}
